import * as THREE from 'three';
import { itemIcon, stockIcon } from './ui/icons.js';
import { showMenu, type MenuOption } from './ui/menu.js';
import { rotateTips } from './ui/tips.js';
import { examineCreature, examineItem, examineNpc, examineObject, fmtCoins, worthOf } from './ui/examine.js';
import { examinePatch, patchOptions, pests, setServerNow, now as farmNow } from './ui/farm.js';
import { PATCHES } from '@sunderfen/shared';
import { BuildMode, buildOn, setBuild, type TownHost } from './ui/build.js';
import { justClosedPopup, popup } from './ui/popups.js';
import {
  BANDS, QUESTS, RESOURCES, isNight, LANTERN_REVEAL_RADIUS, combatLevel, LEGENDS, REVEAL_RADIUS, SKILL_NAMES, SPECIES, TOOLS, bestTool, dayTime, decodeBits, fmtKg, level, revealRadius,
  type FishCtx, type FishRecord, type PileInfo, type LegendId, type SpeciesId, type Weather,
  CREATURES, GEAR, ITEMS, creatureDef, creatureDefence, hitChance, isCreatureKind, playerAttack, type Hit,
  type ActionInfo, type ItemId, type PestKind, type ActorInfo, type ClientMsg, type GameEvent, type GameMap, type MapObject, type SelfPatch, type SelfState, type ServerMsg, type SkillId, type CharacterState,
} from '@sunderfen/shared';
import type { Transport } from './net.js';
import { WorldView, tileColors } from './render/world-view.js';
import { dayLabel } from './render/look.js';
import { AutoQuality, startingQuality, type QualityName } from './render/quality.js';
import { Feel } from './feel.js';
import { audio } from './audio/engine.js';
import { Settings } from './ui/settings.js';
import { ActorView } from './render/actor-view.js';
import { Hud, trackedQuest, type HudHost } from './ui/hud.js';
import { questsOffered, questPeople } from '@sunderfen/shared';
import { setupGuide } from './ui/guide.js';
import { Hints } from './ui/hints.js';
import { POI_KINDS, WorldMap } from './ui/worldmap.js';
import { Maps } from './ui/maps.js';
import { ExchangeUi } from './ui/exchange.js';
import { TradeUi } from './ui/trade.js';
import { sfx } from './audio/sfx.js';
declare const __CLIENT_BUILD__: string;
/** Which build is running: the client's own stamp, and the server's when it differs. Corner label and Settings. */
function showBuild(server?: string) {
  const client = typeof __CLIENT_BUILD__ === 'string' ? __CLIENT_BUILD__ : 'dev';
  const short = (s: string) => s.split(' ')[0];
  const text = !server || server === 'offline' || short(server) === short(client) ? `build ${client}` : `client ${client} · server ${server}`;
  const tag = document.getElementById('build-tag'); if (tag) tag.textContent = text;
  const info = document.getElementById('build-info'); if (info) info.textContent = `Version: ${text}`;
}
showBuild();
setupGuide();
import type { WorldInfo } from './api.js';

const $ = <T extends HTMLElement = HTMLElement>(s: string) => document.querySelector(s) as T;
const clamp = (v: number, a: number, b: number) => (v < a ? a : v > b ? b : v);

export interface GameExit { reason: string; kicked: boolean }

/**
 * The client side of one world session. It renders what the server says and
 * forwards what the player clicks. It never changes game state on its own.
 */
export class Game implements HudHost {
  self!: SelfState;
  tickMs = 600;
  private conn: Transport;
  private view: WorldView;
  private hud: Hud;
  private hints = new Hints();
  private sawNight = false;
  private trade!: TradeUi;
  private exchange!: ExchangeUi;
  openExchange() { this.exchange.request(); }
  private maps!: Maps;
  private me = { id: 0, name: '' };
  private actors = new Map<number, ActorView>();
  private depleted = new Set<number>();
  private cleared = new Set<number>();
  private tick = 0;
  private tickAt = 0;
  private ready = false;
  private last = performance.now();
  private hoverAt: { x: number; y: number } | null = null;
  private hoverClock = 0;
  private keys = { l: false, r: false };
  private raf = 0;
  private feel: Feel;
  private auto: AutoQuality | null = null;
  private settings: Settings;
  weather: Weather = 'clear';
  records: Record<string, FishRecord> = {};
  private dayT = 0.1;
  private biteUntil = 0;
  private biteAt = 0;
  /** A glinting weak point on the rock: strike before this (ms), measured from when it showed. */
  private glintUntil = 0;
  private glintAt = 0;
  /** The current action as the server describes it (the forge's heat lives here). */
  private action: ActionInfo | null = null;
  /** When the bar last came out of the fire (for strike timing). */
  private forgeOutAt = 0;
  private target: number | null = null;
  private lastSpecial?: number;
  private voiceClock = 0;

  /** Build mode (`?build`, offline only): laying out towns. */
  private build: BuildMode | null = null;
  constructor(readonly map: GameMap, private world: WorldInfo, conn: Transport, private onExit: (e: GameExit) => void, private towns?: TownHost) {
    $('#world-name').textContent = `${world.id} · ${world.name}`;
    const pref = Settings.quality();
    const quality: QualityName = pref === 'auto' ? startingQuality() : pref;
    if (pref === 'auto') this.auto = new AutoQuality(quality);
    this.view = new WorldView($<HTMLCanvasElement>('#view'), map, quality);
    this.feel = new Feel(this.view, map, this.tickMs);
    this.view.onThunder = (pan, near) => sfx.thunder(pan, near);
    this.view.onBirdsFlush = (x, z) => { const v = this.feel.at(x, z, 18); if (v) sfx.flutter(v); };
    // offline: a switch in Settings for build mode (laying out towns)
    if (towns) {
      const b = document.createElement('button'); b.type = 'button'; b.className = 'btn small'; b.style.marginTop = '8px';
      b.textContent = buildOn() ? 'Leave build mode' : 'Build towns';
      b.title = 'Lay out towns from the salvage kit. Your town saves in this browser.';
      b.addEventListener('click', () => { setBuild(!buildOn()); location.reload(); });
      $('#settings').appendChild(b);
    }
    this.settings = new Settings(q => {
      if (q === 'auto') { this.auto = new AutoQuality(startingQuality()); this.view.setQuality(this.auto.current); }
      else { this.auto = null; this.view.setQuality(q); }
    });
    // keep the health panel and quest box below the top bar, however many rows it wraps onto (narrow phones)
    const stack = () => {
      const top = $('#topbar').getBoundingClientRect().bottom, v = $('#vitals');
      v.style.top = `${Math.round(top + 8)}px`;
      const vb = v.getBoundingClientRect();
      $('#quest-track').style.top = `${Math.round((v.offsetParent ? vb.bottom : top) + 8)}px`;
    };
    new ResizeObserver(stack).observe($('#topbar')); new ResizeObserver(stack).observe($('#vitals'));
    window.addEventListener('resize', stack);
    // every popup: drag it by its title bar, tap outside to close it
    popup($('#modal'), $('#modal .m-head'), 'station', { close: () => this.hud.closeStation(true), canClose: () => !this.action?.forge });
    popup($('#dialog'), $('#dialog .d-head'), 'dialog', { close: () => { this.send({ t: 'choose', option: 'bye' }); this.hud.closeDialog(); }, canClose: () => this.hud.dialogOpen });
    popup($('#trade'), $('#trade .m-head'), 'trade', { outside: false });
    popup($('#exchange'), $('#exchange .m-head'), 'exchange', { outside: false, close: () => this.exchange.close() });
    this.hud = new Hud(this);
    document.getElementById('hints-toggle')?.addEventListener('click', () => this.hints.setOff(!this.hints.off));
    {
      const KEY = 'sunderfen-camera', seg = document.getElementById('cam-seg');
      const set = (m: 'classic' | 'close', save = true) => { this.view.setCamMode(m); if (save) { try { localStorage.setItem(KEY, m); } catch { /* ignore */ } } seg?.querySelectorAll<HTMLElement>('button').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.cam === m))); };
      let saved: string | null = null; try { saved = localStorage.getItem(KEY); } catch { /* ignore */ }
      set(saved === 'close' ? 'close' : 'classic', false);
      seg?.querySelectorAll<HTMLButtonElement>('button').forEach(b => b.addEventListener('click', () => set(b.dataset.cam === 'close' ? 'close' : 'classic')));
    }
    this.trade = new TradeUi(m => this.send(m), () => this.self);
    this.exchange = new ExchangeUi(m => this.send(m), () => this.self);
    this.conn = conn;
    this.conn.onMessage = m => this.handle(m);
    this.conn.onClose = (reason, kicked) => { cancelAnimationFrame(this.raf); this.onExit({ reason, kicked }); };
    this.bindInput();
    // the loading card: a step line, a bar, and tips while the world comes in
    this.stopTips = rotateTips($('#ld-tip'));
    $('#loading').hidden = false; $('#loading').classList.remove('out');
    $('#ld-step').textContent = 'Finding the valley…'; $('#ld-bar').style.width = '25%';
    setTimeout(() => { if (!this.ready) { $('#ld-step').textContent = 'Walking in…'; $('#ld-bar').style.width = '55%'; } }, 500);
    // `?hmdebug` exposes the client for automated tests. It can only send what the UI can.
    if (location.search.includes('hmdebug')) Object.assign(window as unknown as Record<string, unknown>, { __hm: this, __hmIcons: { itemIcon, stockIcon } });
    window.addEventListener('resize', () => this.view.resize());
    this.view.resize();
  }

  /* ---------------- HudHost ---------------- */
  send(m: ClientMsg) { this.conn.send(m); }
  chartedPercent() { return this.maps?.chartedPercent() ?? 0; }
  isDepleted(id: number) { return this.depleted.has(id); }
  logout() { this.conn.close(); }
  isFelled(id: number) { return this.view.isFelled(id); }
  fishCtx(o?: MapObject): FishCtx { return { dayT: this.dayT, weather: this.weather, hidden: !!o?.props?.hidden }; }
  private get char() { return { ...this.self, revealCarry: 0 } as CharacterState; }

  /* ---------------- server messages ---------------- */
  private handle(m: ServerMsg) {
    try { this.handleMsg(m); } catch (e) { this.reportError(`message ${m.t}`, e); }
  }
  private handleMsg(m: ServerMsg) {
    switch (m.t) {
      case 'welcome': {
        showBuild(m.build);
        this.me = m.you; this.self = m.self; this.tickMs = m.tickMs; this.tick = m.tick; this.tickAt = performance.now();
        this.cleared = new Set(m.self.cleared);
        this.view.setCleared(this.cleared);
        if (m.now) setServerNow(m.now);
        this.view.setPlots(m.self.plots ?? {}, farmNow());
        this.maps = new Maps(this.map, tileColors(this.map), decodeBits(m.self.revealed, this.map.width * this.map.height));
        this.worldMap = new WorldMap(this.map, this.maps, $<HTMLCanvasElement>('#bigmap'), $('#map-tip'),
          (x, z, o) => { this.toggleMap(false); if (o && o.type !== 'house' && !(o.type === 'obstacle' && this.cleared.has(o.id))) this.send({ t: 'interact', id: o.id }); else this.send({ t: 'walk', x, z }); },
          note => { $('#map-perk').textContent = note; });
        const info: ActorInfo = { id: m.you.id, name: m.you.name, x: m.self.x, z: m.self.z, anim: 'idle', tool: null, face: 0.9 };
        this.actors.set(info.id, new ActorView(info.id, info.name, info, true, this.view.actorLayer, $('#tags'), this.tickMs));
        this.view.target.set(m.self.x + 0.5, this.map.surfaceY(m.self.x + 0.5, m.self.z + 0.5, this.cleared) + 0.9, m.self.z + 0.5);
        this.view.warm(m.self.x + 0.5, m.self.z + 0.5);
        this.hud.markDirty('skills', 'pack', 'belt', 'quests', 'journal');
        this.weather = m.weather; this.view.setWeather(m.weather); if (m.forecast) this.view.setForecast(m.forecast.next, m.forecast.inS);
        this.view.fishing.setCharts(m.self.charts);
        this.view.fishing.setTraps(m.self.traps);
        this.hud.updateVitals();
        if (m.self.equip.weapon || m.self.equip.head || m.self.equip.body || m.self.equip.legs) this.actors.get(m.you.id)?.setLook({ w: m.self.equip.weapon, a: [0, 0, 0].map((_, i) => { const id = [m.self.equip.head, m.self.equip.body, m.self.equip.legs][i]; return id ? creatureLook(id) : 0; }) as [number, number, number] });
        this.updateRegion();
        this.hud.updateTracker();
        this.hints.start(m.you.name); this.hints.arrived(m.self); this.hints.setOff(this.hints.off);
        this.markQuests();
        this.ready = true;
        this.raf = requestAnimationFrame(t => this.frame(t));
        $('#ld-step').textContent = 'Lighting the lamps…'; $('#ld-bar').style.width = '100%';
        this.prewarmIcons();
        setTimeout(() => { $('#loading').classList.add('out'); setTimeout(() => { $('#loading').hidden = true; this.stopTips?.(); }, 700); }, 900);
        $('#view').focus({ preventScroll: true });
        return;
      }
      case 'tick': {
        this.tick = m.n; this.tickAt = performance.now();
        const now = performance.now();
        for (const a of m.add ?? []) {
          if (this.actors.has(a.id)) continue;
          const av = new ActorView(a.id, a.name, a, a.id === this.me.id, this.view.actorLayer, $('#tags'), this.tickMs);
          av.onImpact = (x, beat) => this.feel.impact(beat, x.pos.x, x.pos.y, x.facing(), x.tool, x.mob, x.isSelf);
          if (a.mob) av.setDanger(combatLevel(this.char));
          this.actors.set(a.id, av);
          if (av.npc) this.markQuests();
        }
        for (const id of m.remove ?? []) { if (id === this.me.id) continue; this.actors.get(id)?.dispose(this.view.actorLayer); this.actors.delete(id); }
        for (const mv of m.moves ?? []) {
          const a = this.actors.get(mv.id);
          if (!a) continue;
          this.feel.move(mv, mv.id === this.me.id, a.pos, a.mob);
          if (mv.look) a.setLook(mv.look);
          if (mv.mood) { a.setMood(mv.mood); if (mv.mood === 'alert' && a.mob) { const v = this.feel.at(a.pos.x, a.pos.y, 20); if (v) sfx.warn(v); } }
          a.applyMove(mv, now, mv.id === this.me.id ? this.toolTier(mv.tool) : 0);
          if (mv.dead) { a.die(now); const v = this.feel.at(a.pos.x, a.pos.y, 18); if (v) sfx.creatureDie(v); if (a.mob && isCreatureKind(a.mob) && v) sfx.creature(creatureDef(a.mob).sound, v); }
          if (mv.id === this.me.id && mv.path.length) {
            const r = revealRadius(this.char, REVEAL_RADIUS, LANTERN_REVEAL_RADIUS);
            for (const [x, z] of mv.path) this.maps.reveal(x, z, r);
            this.updateRegion();
          }
        }
        for (const [id, state] of m.res ?? []) this.setDepleted(id, state === 1, true);
        for (const [id, dx, dz] of m.felled ?? []) { this.view.setFelled(id, dx, dz, true); const o = this.map.objById.get(id); if (o) this.feel.felled(o); }
        for (const [id, x, z] of m.spots ?? []) this.view.fishing.move(id, x, z);
        for (const [id, stage, rich] of m.veins ?? []) this.view.setVein(id, stage, !!rich);
        for (const h of m.hits ?? []) this.showHit(h, now);
        for (const [id, tiles, ticks] of m.tele ?? []) { this.view.combat.telegraph(tiles, (ticks * this.tickMs) / 1000, now); const a = this.actors.get(id); const v = a ? this.feel.at(a.pos.x, a.pos.y, 16) : null; if (v) sfx.warn(v); }
        for (const [id] of m.levels ?? []) {
          const a = this.actors.get(id);
          if (id === this.me.id) this.recolourLevels();
          if (!a) continue;
          this.view.flourish(a.pos.x, a.pos.y, id === this.me.id);
          if (id !== this.me.id) { const v = this.feel.at(a.pos.x, a.pos.y, 20); if (v) sfx.levelUpNear(v); }
        }
        for (const p of m.piles ?? []) this.view.combat.setPile(p);
        for (const id of m.pilesGone ?? []) this.view.combat.removePile(id);
        if (m.n % 15 === 0) this.hud.markDirty('journal');
        if (this.self.fedTicks > 0) this.self.fedTicks--;
        this.hud.tickBelt();
        return;
      }
      case 'resView': {
        const [x0, z0, x1, z1] = m.bounds, list = new Set(m.depleted);
        for (const o of this.map.objects) if (o.type === 'resource' && o.x >= x0 && o.x <= x1 && o.z >= z0 && o.z <= z1) this.setDepleted(o.id, list.has(o.id));
        for (const [id, x, z] of m.spots) this.view.fishing.move(id, x, z);
        for (const [id, stage, rich] of m.veins ?? []) this.view.setVein(id, stage, !!rich);
        for (const [id, dx, dz] of m.felled ?? []) if (!this.view.isFelled(id)) this.view.setFelled(id, dx, dz, false);
        if (m.forest) this.forestHealth = m.forest;
        return;
      }
      case 'forest': this.forestHealth = m.health; return;
      case 'weather': this.weather = m.weather; this.view.setWeather(m.weather); if (m.forecast) this.view.setForecast(m.forecast.next, m.forecast.inS); this.hud.markDirty('journal'); return;
      case 'records': this.records = m.records; this.hud.markDirty('journal'); return;
      case 'self': return this.applyPatch(m.patch);
      case 'events': for (const e of m.list) this.event(e); return;
      case 'station': {
        if (m.id === null) { this.hud.closeStation(); return; }
        const o = this.map.objById.get(m.id);
        if (o) { this.hud.openStation(o); this.feel.station(o.kind); }
        return;
      }
      case 'dialog': {
        if (m.npc === null) this.hud.closeDialog();
        else this.hud.openDialog(m.name ?? '', m.title ?? '', m.kind ?? '', m.text ?? '', m.options ?? []);
        return;
      }
      case 'trade': this.trade.show(m.view); if (m.view) this.hud.closeStation(); return;
      case 'ex': this.exchange.got(m); return;
      case 'shop': {
        this.hud.closeDialog();
        this.hud.openStation({ id: -m.npc, type: 'station', kind: 'merchant', x: 0, z: 0, props: { name: m.name, shop: m.shop } });
        this.feel.station('merchant');
        return;
      }
      case 'chat': {
        this.hud.log(m.text, m.npc ? 'npcsay' : undefined, m.from);
        if (!m.npc) this.feel.chat();
        this.actors.get(m.id)?.say(m.text, performance.now(), $('#tags'));
        return;
      }
      case 'error': this.hud.log(m.text, 'bad'); return;
      case 'kick': case 'pong': return;
    }
  }

  /** Metal of the tool the server picked for an action, so our own hands show the right colour. */
  private toolTier(tool: string | null) {
    if (!tool) return 0;
    const line = tool === 'axe' ? 'axe' : tool === 'pick' ? 'pick' : tool === 'harpoon' ? 'harpoon' : null;
    const t = line ? bestTool(this.self, line) : null;
    return t ? TOOLS[t].metal ?? 0 : 0;
  }

  private setDepleted(id: number, d: boolean, announce = false) {
    if (d === this.depleted.has(id)) return;
    if (announce) { const o = this.map.objById.get(id); if (o) this.feel.resource(o, d); }
    if (d) this.depleted.add(id); else this.depleted.delete(id);
    // trees fall when they're felled (the 'felled' list); depletion is the trunk sawn up or rotted
    this.view.setDepleted(id, d);
  }
  /** Forest health per region (only regions below full are listed). */
  forestHealth: Record<string, number> = {};

  private applyPatch(p: SelfPatch) {
    const { action, ...rest } = p;
    if (p.inv) { const before = this.self.inv, got: number[] = []; p.inv.forEach((id, i) => { if (id && id !== before[i]) got.push(i); }); if (got.length) { this.hud.flashSlots(got); this.hud.markDirty('pack'); } }
    this.feel.patch({ ...this.self, inv: [...this.self.inv] }, p);
    Object.assign(this.self, rest);
    if ('action' in p) {
      const was = this.action?.forge?.phase;
      this.action = action ?? null;
      if (this.action?.forge?.phase === 'work' && was !== 'work') this.forgeOutAt = performance.now();
      if (!this.action?.forge && this.glintUntil && !this.action) this.endGlint();
      this.hud.setAction(action ?? null);
      this.view.setWorking(!!this.action);
    }
    const keys = Object.keys(rest);
    if (keys.some(k => k === 'xp')) this.hud.markDirty('skills', 'belt');
    if (keys.some(k => ['inv', 'stacks', 'coins', 'bank'].includes(k))) { this.hud.markDirty('pack'); this.trade.render(); this.exchange.refresh(); }
    if (keys.some(k => ['inv', 'stacks', 'bank', 'traps', 'upgrades', 'fedTicks', 'regions', 'landmarks', 'xp'].includes(k))) this.hud.markDirty('belt');
    if (keys.some(k => ['journal', 'charts', 'xp', 'finds', 'masterworks', 'order', 'inv'].includes(k))) this.hud.markDirty('quests', 'journal');
    if (keys.some(k => ['sack', 'wear'].includes(k))) this.hud.markDirty('pack', 'belt');
    if (keys.some(k => ['quests', 'inv', 'stacks'].includes(k))) this.hud.updateTracker();
    if (keys.some(k => ['quests', 'standing', 'flags', 'board', 'contract'].includes(k))) this.hud.markDirty('quests', 'journal');
    if (keys.some(k => ['run', 'energy', 'sneak', 'autoRetaliate'].includes(k))) this.hud.updateRun();
    if (keys.some(k => ['friends', 'friendsOnline'].includes(k))) this.hud.markDirty('friends');
    if (keys.some(k => ['quests', 'flags', 'inv'].includes(k))) this.goalClock = 0;
    if (keys.some(k => ['quests', 'xp'].includes(k))) this.markQuests();
    if (keys.some(k => ['hp', 'special', 'stance', 'buffs', 'regen', 'equip', 'xp'].includes(k))) this.hud.updateVitals();
    if (keys.some(k => ['inv', 'hp', 'quests', 'flags'].includes(k))) this.hints.watch(this.self);
    if (p.bank) this.hints.banked();
    if (p.equip) { this.hud.markDirty('belt', 'pack'); sfx.equip(); }
    if (p.special !== undefined && p.special < (this.lastSpecial ?? 100)) { this.hud.specQueued = false; this.hud.updateVitals(); }
    if (p.special !== undefined) this.lastSpecial = p.special;
    if ('target' in p) this.target = p.target ?? null;
    if (keys.some(k => ['inv', 'stacks', 'bank', 'coins', 'upgrades', 'xp', 'sack', 'equip'].includes(k))) this.hud.markDirty();
    if (p.traps) this.view.fishing.setTraps(p.traps);
    if (p.charts) {
      this.view.fishing.setCharts(p.charts);
      for (const id of p.charts) { const o = this.map.objById.get(id); if (o) this.maps.reveal(o.x, o.z, 6); }
    }
    if (p.cleared) { this.cleared = new Set(p.cleared); this.view.setCleared(this.cleared); }
    if (p.plots) this.view.setPlots(p.plots, farmNow());
  }

  private event(e: GameEvent) {
    const me = this.actors.get(this.me.id);
    if (me) this.feel.event(e, { x: me.pos.x, y: me.pos.y, face: me.model.root.rotation.y });
    const screen = () => {
      if (!me) return [0, 0];
      const v = new THREE.Vector3(me.pos.x, this.map.surfaceY(me.pos.x, me.pos.y, this.cleared) + 2.2, me.pos.y).project(this.view.camera);
      const r = this.view.canvas.getBoundingClientRect();
      return [((v.x + 1) / 2) * r.width, ((1 - v.y) / 2) * r.height];
    };
    switch (e.kind) {
      case 'log': this.hud.log(e.text, e.tone); break;
      case 'toast': this.hud.toast(e.title, e.text, e.key); break;
      case 'banner': this.hud.banner(e.eyebrow, e.title, e.text); break;
      case 'quest': {
        sfx.quest(e.state); this.hud.updateTracker(true); this.hud.markDirty('quests', 'journal');
        const def = QUESTS[e.id];
        if (def && e.state === 'start' && e.id !== 'gull_rock') this.hud.banner('New quest', def.name, def.blurb);
        if (e.state === 'step') this.hud.stepDone();
        // off the island: the valley opens up
        if (e.state === 'done' && e.id === 'gull_rock') setTimeout(() => { this.hud.banner('The valley of', 'Sunderfen', 'Meadowfold is up the road. People there could use a hand.'); sfx.masterwork(); }, 4300);
        this.hints.quest(e.state, e.id);
        break;
      }
      case 'xp': this.hud.xpDrop(e.skill, e.amount); void screen; break;
      case 'levelup': this.hud.levelUp(e.skill, e.level, e.unlocks, e.next); this.hints.levelUp(); break;
      case 'pm': {
        if (e.to) this.hud.log(e.text, 'pm', `To ${e.to}`);
        else { this.lastPmFrom = e.from; this.hud.log(e.text, 'pm', `From ${e.from}${e.world ? ` (${e.world})` : ''}`); sfx.pm(); }
        break;
      }
      case 'item': {
        const it = ITEMS[e.item], rare = !!it && (it.cat === 'valuable' || it.cat === 'treasure' || it.cat === 'trophy');
        const [x, y] = screen(); this.hud.itemPop(e.item, x, y, rare);
        if (rare && me) { sfx.find(); this.view.particles.sparkle(me.pos.x, this.map.heightAt(me.pos.x, me.pos.y) + 0.4, me.pos.y); }
        break;
      }
      case 'catch': {
        const sp = SPECIES[e.species as SpeciesId], lg = LEGENDS[e.species as LegendId];
        const name = lg ? lg.name : e.n && e.n > 1 ? sp.plural : sp.name.toLowerCase();
        let text = lg ? `You land ${lg.name}! ${fmtKg(e.kg)}.` : e.n && e.n > 1 ? `You net ${e.n} ${name} (best ${fmtKg(e.kg)}).` : `You catch a ${fmtKg(e.kg)} ${name}.`;
        if (e.strike) text = `Good strike! ${text}`;
        if (e.wr) text += ' A world record!'; else if (e.pb) text += ' A personal best!';
        this.hud.log(text, e.wr || e.pb || lg ? 'lv' : 'ok');
        if (e.wr) this.hud.toast('World record', `${fmtKg(e.kg)} ${lg ? lg.name : sp.name.toLowerCase()}. Your name is in the Journal.`, 'record');
        else if (e.pb) this.hud.toast('Personal best', `${fmtKg(e.kg)} ${sp?.name.toLowerCase() ?? name}`, 'pb');
        const [x, y] = screen(); this.hud.floatText(fmtKg(e.kg), x, y - 22);
        this.endBite();
        break;
      }
      case 'bite': {
        const ms = (e.until - this.tick + 0.6) * this.tickMs;
        this.biteAt = performance.now(); this.biteUntil = this.biteAt + ms;
        me?.bite(this.biteUntil);
        $('#strike').firstChild!.textContent = 'Strike! ';
        $('#strike').hidden = false;
        break;
      }
      case 'trap': break;
      case 'pest': {
        const o = this.map.objById.get(e.obj);
        if (!o) break;
        if (e.ms) pests.set(e.obj, { critter: e.critter as PestKind, until: performance.now() + e.ms }); else pests.delete(e.obj);
        this.view.showPest(o, e.critter, e.ms);
        break;
      }
      case 'glint': {
        // a weak point shines on the rock: strike it (button or space) while it lasts
        this.glintAt = performance.now(); this.glintUntil = this.glintAt + e.ms;
        this.view.showGlint(e.obj, e.ms);
        const b = $('#strike'); b.firstChild!.textContent = 'Strike the seam! '; b.hidden = false;
        sfx.glint();
        break;
      }
      case 'find': {
        const it = ITEMS[e.item];
        this.hud.toast(e.glint ? 'A find in the seam' : 'A find', `${it?.name ?? e.item}${e.glint ? ', knocked loose by a clean strike' : ''}.`, `find-${e.item}`);
        sfx.find();
        this.hud.markDirty('journal');
        break;
      }
      case 'masterwork': {
        this.hud.banner('Masterwork', `“${e.name}”`, `${ITEMS[e.item as ItemId]?.name ?? 'A piece'} with your mark on it. It's in your pack, and in your journal.`);
        sfx.masterwork();
        this.hud.markDirty('journal');
        break;
      }
      case 'wear': if (e.state === 'broken') sfx.clang(); this.hud.markDirty('pack', 'belt'); break;
      case 'loot': {
        this.hud.logLoot(e.creature, e.items);
        const order = ['very_rare', 'rare', 'shared', 'uncommon', 'common', 'always'];
        const best = e.items.map(i => i.rarity).sort((a, b) => order.indexOf(a) - order.indexOf(b))[0];
        if (best) sfx.drop(best);
        if (best === 'very_rare' || best === 'rare' || best === 'shared') { const it = e.items.find(i => i.rarity === best)!; this.hud.toast(best === 'very_rare' ? 'Very rare drop!' : 'Rare drop', `${it.item === 'coins' ? `${it.n} coins` : ITEMS[it.item].name}`, 'drop'); }
        break;
      }
      case 'died': sfx.death(); this.target = null; this.hints.died(); break;
      case 'tell': this.hud.log(e.text, 'tell'); break;
      case 'tradeAsk': this.trade.asked(e.from, e.id); this.hud.log(`${e.from} wants to trade with you.`, 'ok'); break;
    }
  }

  /** One attack landed (or missed): projectile, splat, sound, hp bar. */
  /** Put ! over everyone with a quest you can take, ? over everyone your current steps need. */
  private markQuests() {
    if (!this.self) return;
    const c = this.self as unknown as CharacterState, givers = new Set(questsOffered(this.self, k => level(c, k)).map(id => QUESTS[id].giver)), need = questPeople(this.self);
    for (const a of this.actors.values()) if (a.npc) a.setQuestMark(need.has(a.npc) ? 'step' : givers.has(a.npc) ? 'offer' : null);
  }
    private showHit(h: Hit, now: number) {
    const from = this.actors.get(h.from), to = this.actors.get(h.to);
    if (!to) return;
    const land = () => {
      to.setHp(h.hp, h.max, performance.now());
      to.react(!!h.miss, performance.now());
      if (h.to === this.me.id && !h.miss) this.view.shake(Math.min(0.28, 0.06 + (h.dmg / Math.max(1, h.max)) * 0.8));
      else if (h.from === this.me.id && h.special && !h.miss) this.view.shake(0.14);
      // a heavy blow of yours lands with a little weight: a nudge, and the target flashes
      else if (h.from === this.me.id && !h.miss && h.dmg >= h.max * 0.2) this.view.shake(0.05);
      const v = this.feel.at(to.pos.x, to.pos.y, 16);
      if (h.miss) { if (v) sfx.miss(v); }
      else if (h.style === 'ranged') { if (v) sfx.arrowHit(v); }
      else if (h.style === 'magic') { if (v) sfx.frost(v); }
      else if (v) sfx.hit(v, h.dmg > h.max * 0.15);
      if (h.to === this.me.id && !h.miss) { sfx.hurt(); this.view.particles.emit({ x: to.pos.x, y: this.map.heightAt(to.pos.x, to.pos.y) + 1, z: to.pos.y, count: 6, spread: 0.2, vy: 1.4, vSpread: 1.2, colors: [0xc83a2a, 0x8a1a12], size: 0.06, life: 0.5, gravity: 7 }); }
      if (h.from === this.me.id && !h.miss && to.mob) this.view.particles.emit({ x: to.pos.x, y: this.map.heightAt(to.pos.x, to.pos.y) + 0.6, z: to.pos.y, count: 5, spread: 0.2, vy: 1.4, vSpread: 1.2, colors: [0xffe6a0, 0xffffff], size: 0.05, life: 0.3, gravity: 6 }, true);
      const r = this.view.canvas.getBoundingClientRect();
      const p = new THREE.Vector3(to.pos.x, to.model.root.position.y + (to.cm ? to.cm.tagY * 0.6 : 1.1), to.pos.y).project(this.view.camera);
      this.hud.splat(h.miss ? '0' : String(h.dmg), ((p.x + 1) / 2) * r.width, ((1 - p.y) / 2) * r.height, h.miss ? 'miss' : h.to === this.me.id ? '' : h.from === this.me.id ? `mine${h.special ? ' big' : ''}` : '', h.special);
    };
    if (h.special) { const v = from ? this.feel.at(from.pos.x, from.pos.y, 16) : null; if (v) sfx.special(v); }
    if (from && (h.style === 'ranged' || h.style === 'magic')) {
      const y0 = this.map.heightAt(from.pos.x, from.pos.y) + 1.1, y1 = this.map.heightAt(to.pos.x, to.pos.y) + 0.8;
      const kind = h.style === 'magic' ? 'bolt' : from.mob === 'goblin' ? 'rock' : 'arrow';
      if (kind === 'arrow' && from.cm) { const v = this.feel.at(from.pos.x, from.pos.y, 16); if (v) sfx.bowRelease(v); }
      const fire = () => this.view.combat.shoot(new THREE.Vector3(from.pos.x, Math.max(y0, 0.5), from.pos.y), new THREE.Vector3(to.pos.x, Math.max(y1, 0.4), to.pos.y), kind, land);
      // archers loose on the release frame of their draw
      const wait = from.cm ? 0 : from.impactDelay();
      if (wait > 20) setTimeout(fire, wait); else fire();
    } else setTimeout(land, from ? from.impactDelay() : 150);
    void now;
  }

  /** One strike key for everything: a bite on the line, a glint in the rock, the bar on the anvil. */
  strike() {
    const now = performance.now();
    if (now < this.biteUntil) { this.send({ t: 'strike', rt: Math.round(now - this.biteAt) }); this.endBite(); return; }
    if (now < this.glintUntil) { this.send({ t: 'strike', rt: Math.round(now - this.glintAt) }); this.endGlint(); return; }
    const f = this.action?.forge;
    if (f) { this.send({ t: 'strike', ...(f.phase === 'work' ? { rt: Math.round(now - this.forgeOutAt) } : {}) }); return; }
  }
  private endGlint() { this.glintUntil = 0; this.view.clearGlint(); if (!this.biteUntil) { const b = $('#strike'); b.hidden = true; b.firstChild!.textContent = 'Strike! '; } }
  get striking() { const now = performance.now(); return now < this.biteUntil || now < this.glintUntil || !!this.action?.forge; }
  private endBite() { this.biteUntil = 0; $('#strike').hidden = true; this.actors.get(this.me.id)?.bite(0); }

  private updateRegion() {
    const me = this.actors.get(this.me.id);
    const x = me ? me.tileX : this.self.x, z = me ? me.tileZ : this.self.z;
    const r = this.map.regionAt(x, z);
    if (!r) return;
    // a named place inside the region (a farm, a clearing): its name leads, the region follows
    const place = this.map.placeAt(x, z);
    this.hud.setRegion(place ? `${place.name} · ${r.name}` : r.name, r.band);
    const pk = place?.name ?? '';
    if (pk !== this.lastPlace) {
      const had = this.lastPlace !== null;
      this.lastPlace = pk;
      if (place && had) this.hud.toast(place.name, place.blurb ?? r.name, 'place');
    }
    if (r.key === this.lastRegion) return;
    const first = this.lastRegion === '';
    this.lastRegion = r.key;
    // walking into somewhere dangerous: say so, and how dangerous for *you*
    if (first || !r.band || r.band === 'home') return;
    const mine = combatLevel(this.char), advise = r.advise ?? 0, under = advise > 0 && mine < advise;
    const parts = [BANDS[r.band].name, r.held, advise ? `combat ${advise}+ advised` : ''].filter(Boolean).join(' · ');
    this.hud.toast(r.name, under ? `${parts}. You're combat ${mine}: take care.` : parts, 'region', under);
  }
  private lastRegion = '';
  private lastPlace: string | null = null;
  /** Recolour every creature's level tag against our combat level. */
  private recolourLevels() { const mine = combatLevel(this.char); for (const a of this.actors.values()) a.setDanger(mine); }

  /* ---------------- frame ---------------- */
  private nextDraw = 0;
  /** One frame, always followed by the next: a bug in one part of a frame must never freeze the game. */
  private frame(ts: number) {
    this.raf = requestAnimationFrame(t => this.frame(t));
    try { this.frameBody(ts); } catch (e) { this.reportError('frame', e); }
  }
  /** Draw the icons of everything you carry and keep, a couple at a time while the browser is idle, so opening the pack or the bank never stalls. */
  private prewarmIcons() {
    const ids = [...new Set([...this.self.inv.filter(Boolean), ...Object.keys(this.self.bank), ...Object.values(this.self.equip).filter(Boolean)])] as string[];
    const idle = (fn: () => void) => ((window as unknown as { requestIdleCallback?: (f: () => void) => void }).requestIdleCallback ?? ((f: () => void) => setTimeout(f, 60)))(fn);
    const step = () => { for (let k = 0; k < 2 && ids.length; k++) { const id = ids.shift()!; try { itemIcon(id as ItemId); } catch { /* drawn later, or flat */ } } if (ids.length) idle(step); };
    idle(step);
  }
  private errorsShown = new Set<string>();
  /** Something went wrong: say so once (with where), keep going. */
  private reportError(where: string, e: unknown) {
    const msg = String((e as Error)?.message ?? e);
    console.error(`[sunderfen] ${where}:`, e);
    if (!this.errorsShown.has(where + msg)) { this.errorsShown.add(where + msg); this.hud?.log(`Something went wrong (${where}: ${msg}). The game carried on; if anything looks stuck, reload.`, 'bad'); }
  }
  private plotClock = 0;
  private frameBody(ts: number) {
    // the frame cap: skip this refresh if it's too soon (keeps fast monitors from running the GPU flat out)
    const cap = this.settings.fpsCap;
    if (cap) {
      if (ts < this.nextDraw - 2) return;
      this.nextDraw += 1000 / cap;
      if (this.nextDraw < ts) this.nextDraw = ts + 1000 / cap; // fell behind (tab was hidden, a long frame): don't try to catch up
    }
    const dt = Math.min(0.1, Math.max(0, (ts - this.last) / 1000));
    this.last = ts;
    if (this.keys.l) this.view.yaw -= dt * 1.8;
    if (this.keys.r) this.view.yaw += dt * 1.8;
    const now = performance.now(), rect = this.view.canvas.getBoundingClientRect();
    const fracTick = this.tick + Math.min(1, (now - this.tickAt) / this.tickMs);
    for (const a of this.actors.values()) a.update(dt, now, this.map, a.isSelf ? this.cleared : this.view.allObstacles, this.view.camera, rect);
    this.groundTags(rect);
    // crops grow on the clock: look again now and then (only patches whose look changed are rebuilt)
    if ((this.plotClock -= dt) <= 0) { this.plotClock = 5; this.view.setPlots(this.self.plots ?? {}, farmNow()); }
    const me = this.actors.get(this.me.id);
    if (!this.build && this.towns && me && buildOn()) this.build = new BuildMode(this.view, this.map, this.towns, new THREE.Vector3(me.pos.x, 0, me.pos.y));
    this.build?.update(dt);
    const focus = this.build ? this.build.focus.clone() : me ? new THREE.Vector3(me.pos.x, this.map.surfaceY(me.pos.x, me.pos.y, this.cleared) + 0.9, me.pos.y) : this.view.target.clone();
    // window.hmDayOffset lets you preview other times of day from the browser console.
    const dayT = (dayTime(fracTick, this.tickMs) + ((window as unknown as { hmDayOffset?: number }).hmDayOffset ?? 0)) % 1;
    this.dayT = dayT;
    if (this.biteUntil && now > this.biteUntil) this.endBite();
    if (this.glintUntil && now > this.glintUntil) this.endGlint();
    this.view.walkers = [];
    for (const a of this.actors.values()) if (!a.deadAt) this.view.walkers.push({ x: a.pos.x, z: a.pos.y, moving: a.moving });
    const foe = this.target !== null ? this.actors.get(this.target) : undefined;
    this.view.combat.setRing(foe && !foe.deadAt && foe.cm ? { x: foe.pos.x, z: foe.pos.y, size: Math.max(0.8, foe.cm.tagY * 0.6) } : null, now);
    this.view.frame(dt, now / 1000, dayT, focus);
    for (const a of this.actors.values()) a.placeTags(now, this.view.camera, rect);
    if (me) {
      const v = new THREE.Vector3(me.pos.x, me.model.root.position.y + 2.45, me.pos.y).project(this.view.camera);
      this.hud.placeRing(((v.x + 1) / 2) * rect.width, ((1 - v.y) / 2) * rect.height, v.z < 1);
    }
    this.hud.setClock(dayLabel(dayT) + (this.view.rainComing > 0.05 ? ' · rain soon' : ''));
    if (me) this.feel.listen(me.pos.x, me.pos.y, this.view.yaw);
    this.feel.frame(dt, now / 1000, this.view.night, this.view.fishing.wet);
    if (!this.sawNight && this.view.night > 0.7) { this.sawNight = true; this.hints.night(); }
    this.voiceClock -= dt;
    if (this.voiceClock <= 0 && me) {
      this.voiceClock = 1.2;
      const near = [...this.actors.values()].filter(a => a.mob && !a.deadAt && Math.hypot(a.pos.x - me.pos.x, a.pos.y - me.pos.y) < 14);
      const a = near[Math.floor(Math.random() * near.length)];
      if (a && Math.random() < 0.25 && isCreatureKind(a.mob!)) { const v = this.feel.at(a.pos.x, a.pos.y, 14); if (v) sfx.creature(creatureDef(a.mob!).sound, v); }
    }
    this.settings.frame(dt);
    if (this.auto) { const next = this.auto.sample(dt); if (next) this.view.setQuality(next); }
    this.hud.updateActionBar(fracTick);
    this.hud.render();
    this.goalClock -= dt;
    if (this.goalClock <= 0) { this.goalClock = 0.25; this.goal = this.questGoal(); this.view.setGoal(this.goal); }
    if (me) this.maps.drawMini($<HTMLCanvasElement>('#minimap'), this.mapState(), $('#compass'));
    if (this.worldMap?.open) { this.mapClock -= dt; if (this.mapClock <= 0) { this.mapClock = 0.4; this.worldMap.update(this.mapState()); } }
    this.hover(dt);
  }

  private mapState() {
    const me = this.actors.get(this.me.id)!;
    return { px: me.pos.x, pz: me.pos.y, face: me.model.root.rotation.y, yaw: this.view.yaw, regions: this.self.regions, landmarks: this.self.landmarks, cleared: this.cleared, depleted: this.depleted, surveyor: level(this.char, 'exploration') >= 10, charts: this.self.charts, traps: this.self.traps, goal: this.goal ? [this.goal[0], this.goal[1]] as [number, number] : null, waystones: this.self.waystones, pulse: performance.now() / 1000 };
  }

  /* ---------------- quest goal ---------------- */
  private goal: [number, number, number] | null = null;
  private goalClock = 0;
  /**
   * Where the step you're on wants you: the person to talk to, the nearest creature to fight,
   * the thing to use, the place to reach. Steps that are about what you carry point nowhere
   * unless the quest says where (the furnace, the chest).
   */
  private questGoal(): [number, number, number] | null {
    const s = this.self, me = this.actors.get(this.me.id);
    const id = trackedQuest(s);
    if (!id || !me) return null;
    const step = QUESTS[id].steps[s.quests[id].step];
    if (!step) return null;
    const w = step.where ?? {}, px = me.pos.x, pz = me.pos.y;
    const sameIsle = (x: number, z: number) => (this.map.regionAt(x, z)?.key === 'gull') === (this.map.regionAt(px, pz)?.key === 'gull');
    const nearestObj = (pred: (o: MapObject) => boolean) => { let best: MapObject | null = null, bd = Infinity; for (const o of this.map.objects) if (pred(o) && sameIsle(o.x, o.z)) { const d = Math.hypot(o.x - px, o.z - pz); if (d < bd) { bd = d; best = o; } } return best; };
    const centre = (o: MapObject, h: number): [number, number, number] => [o.x + (o.w ?? 1) / 2, o.z + (o.h ?? 1) / 2, h];
    const npc = w.npc ?? (step.kind === 'talk' || step.kind === 'bring' ? step.npc : undefined);
    if (npc) {
      for (const a of this.actors.values()) if (a.npc === npc) { if (a.indoors) this.hints.knock(); return [a.pos.x, a.pos.y, 2.2]; }
      const home = nearestObj(o => o.type === 'npc' && o.kind === npc);
      return home ? centre(home, 2.2) : null;
    }
    const mob = w.mob ?? (step.kind === 'kill' ? step.mob : undefined);
    if (mob) {
      let best: [number, number, number] | null = null, bd = Infinity;
      for (const a of this.actors.values()) if (a.mob === mob && !a.deadAt && sameIsle(a.pos.x, a.pos.y)) { const d = Math.hypot(a.pos.x - px, a.pos.y - pz); if (d < bd) { bd = d; best = [a.pos.x, a.pos.y, (a.cm?.tagY ?? 1.4) + 0.2]; } }
      if (best) return best;
      const lair = nearestObj(o => o.type === 'creature' && o.kind === mob);
      return lair ? centre(lair, 1.4) : null;
    }
    const near = step.kind === 'reach' ? step.near : undefined;
    const obj = w.obj ?? near?.obj, name = w.name ?? near?.name;
    if (obj) { const o = nearestObj(o => o.kind === obj && (!name || o.props?.name === name)); return o ? centre(o, 2) : null; }
    const region = w.region ?? (step.kind === 'reach' ? step.region : undefined);
    if (region) { const r = this.map.regions.find(r => r.key === region); return r ? [r.label[0], r.label[1], 2] : null; }
    if (step.kind === 'reach' && step.at) return [step.at[0], step.at[1], 2];
    return null;
  }

  /* ---------------- input ---------------- */
  private click(clientX: number, clientY: number, shift = false, alt = false, touch = false) {
    if (!this.ready) return;
    if (justClosedPopup()) return; // that tap was to close a popup, not to walk
    if (this.build) { this.build.click(clientX, clientY, shift, alt, touch); return; }
    const hit = this.view.pick(clientX, clientY);
    if (!hit) return;
    this.hud.closeStation();
    if (hit.pile !== undefined) { this.send({ t: 'take', pile: hit.pile, i: -1 }); const pi = this.view.combat.pileInfo(hit.pile); if (pi) this.view.showMarker(pi.x + 0.5, pi.z + 0.5); return; }
    if (hit.trap !== undefined) { this.send({ t: 'haul', i: hit.trap }); const t = this.self.traps[hit.trap]; if (t) this.view.showMarker(t.x + 0.5, t.z + 0.5); return; }
    const actor = hit.actor !== undefined ? this.actors.get(hit.actor) : undefined;
    if (actor?.mob) {
      this.send({ t: 'attack', id: actor.id });
      this.view.showMarker(actor.pos.x, actor.pos.y);
      return;
    }
    if (actor && !actor.npc && !actor.mob && !actor.isSelf) {
      this.send({ t: 'tradeAsk', id: actor.id });
      this.view.showMarker(actor.pos.x, actor.pos.y);
      return;
    }
    if (actor?.npc) {
      this.send({ t: 'talk', id: actor.id });
      this.view.showMarker(actor.pos.x, actor.pos.y);
      return;
    }
    const clutter = (hit.obj?.type === 'prop' && hit.obj.kind !== 'stall' && hit.obj.kind !== 'signpost') || (hit.obj?.type === 'house' && !isNight(this.dayT));
    if (hit.obj?.type === 'patch') { this.send({ t: 'farm', id: hit.obj.id, op: 'auto' }); this.view.markObject(hit.obj); return; }
    if (hit.obj && !clutter && !(hit.obj.type === 'obstacle' && this.cleared.has(hit.obj.id))) {
      this.send({ t: 'interact', id: hit.obj.id });
      if (hit.obj.type === 'resource' || hit.obj.type === 'station' || hit.obj.type === 'obstacle') this.view.markObject(hit.obj);
      else this.view.showMarker(hit.obj.x + (hit.obj.w ?? 1) / 2, hit.obj.z + (hit.obj.h ?? 1) / 2);
    } else if (hit.tile) {
      this.send({ t: 'walk', x: hit.tile[0], z: hit.tile[1] });
      this.view.showMarker(hit.tile[0] + 0.5, hit.tile[1] + 0.5);
    }
  }

  /**
   * Names over things on the ground, coloured by worth like RuneScape's ground-item labels: plain
   * under 1k, then green, blue, orange and pink as it climbs. "+3 more" when there's a heap; the
   * total value when it's worth mentioning. Only the nearest dozen, so a battlefield stays legible.
   */
  private tagPool: HTMLElement[] = [];
  private groundTags(rect: DOMRect) {
    const me = this.actors.get(this.me.id), layer = $('#tags');
    const piles = me ? [...this.view.combat.piles.values()].map(g => g.userData.info as PileInfo).filter(p => p && Math.hypot(p.x + 0.5 - me.pos.x, p.z + 0.5 - me.pos.y) < 16)
      .sort((a, b) => Math.hypot(a.x - me!.pos.x, a.z - me!.pos.y) - Math.hypot(b.x - me!.pos.x, b.z - me!.pos.y)).slice(0, 12) : [];
    const v = new THREE.Vector3();
    piles.forEach((p, i) => {
      let el = this.tagPool[i];
      if (!el) { el = document.createElement('div'); el.className = 'ground-tag'; layer.appendChild(el); this.tagPool[i] = el; }
      const key = `${p.id}:${JSON.stringify(p.items)}`;
      if (el.dataset.key !== key) {
        el.dataset.key = key;
        const sorted = [...p.items].sort((a, b) => worthOf(b.item) * b.n - worthOf(a.item) * a.n), top = sorted[0];
        const total = p.items.reduce((t, e) => t + worthOf(e.item) * e.n, 0), best = top ? worthOf(top.item) * top.n : 0;
        const name = !top ? '' : top.item === 'coins' ? `${top.n.toLocaleString()} coins` : `${ITEMS[top.item]?.name ?? top.item}${top.n > 1 ? ` ×${top.n.toLocaleString()}` : ''}`;
        el.textContent = `${name}${p.items.length > 1 ? ` +${p.items.length - 1} more` : ''}${total >= 1000 ? ` · ${fmtCoins(total)}` : ''}`;
        el.className = `ground-tag v${best >= 1e6 ? 4 : best >= 1e5 ? 3 : best >= 1e4 ? 2 : best >= 1000 ? 1 : 0}${p.kind === 'grave' ? ' grave' : ''}`;
      }
      v.set(p.x + 0.5, Math.max(0, this.map.heightAt(p.x + 0.5, p.z + 0.5)) + 0.55, p.z + 0.5).project(this.view.camera);
      const on = v.z < 1 && Math.abs(v.x) < 1.05 && Math.abs(v.y) < 1.05;
      el.hidden = !on;
      if (on) el.style.transform = `translate(${((v.x + 1) / 2) * rect.width}px, ${((1 - v.y) / 2) * rect.height}px) translate(-50%, -100%)`;
    });
    for (let i = piles.length; i < this.tagPool.length; i++) this.tagPool[i].hidden = true;
  }

  /** Everything you can do with what's under (x, y), RuneScape-style. */
  private openMenu(x: number, y: number) {
    if (!this.ready) return;
    const hit = this.view.pick(x, y);
    if (!hit) return;
    this.hud.tooltip(null);
    const opts: MenuOption[] = [], mark = (px: number, pz: number) => this.view.showMarker(px, pz);
    const exam = (text: string) => this.hud.log(text, 'lore');
    let title = '';
    if (hit.pile !== undefined) {
      const pi = this.view.combat.pileInfo(hit.pile);
      if (pi) {
        title = pi.kind === 'grave' ? (pi.mine ? 'Your grave' : `${pi.owner}'s grave`) : 'On the ground';
        pi.items.slice(0, 10).forEach((it, i) => {
          const name = it.item === 'coins' ? 'Coins' : ITEMS[it.item]?.name ?? it.item;
          opts.push({ label: `Take ${name}${it.n > 1 ? ` (${it.n.toLocaleString()})` : ''}`, sub: worthOf(it.item) ? fmtCoins(worthOf(it.item) * it.n) : undefined, act: () => { this.send({ t: 'take', pile: pi.id, i }); mark(pi.x + 0.5, pi.z + 0.5); } });
        });
        if (pi.items.length > 1) opts.push({ label: 'Take everything', act: () => { this.send({ t: 'take', pile: pi.id, i: -1 }); mark(pi.x + 0.5, pi.z + 0.5); } });
        const first = pi.items[0];
        if (first) opts.push({ label: `Examine ${first.item === 'coins' ? 'coins' : (ITEMS[first.item]?.name ?? '').toLowerCase()}`, tone: 'muted', act: () => exam(examineItem(first.item)) });
      }
    }
    const actor = hit.actor !== undefined ? this.actors.get(hit.actor) : undefined;
    if (!opts.length && actor && !actor.isSelf) {
      title = actor.name;
      if (actor.mob && isCreatureKind(actor.mob)) {
        const d = creatureDef(actor.mob), pa = playerAttack(this.char), chance = Math.round(hitChance(pa.acc, creatureDefence(d)) * 100);
        opts.push({ label: `Attack ${d.name}`, sub: `${chance}% to hit`, tone: 'attack', act: () => { this.send({ t: 'attack', id: actor.id }); mark(actor.pos.x, actor.pos.y); } });
        opts.push({ label: `Examine ${d.name.toLowerCase()}`, tone: 'muted', act: () => exam(examineCreature(actor.mob!)) });
      } else if (actor.npc) {
        opts.push({ label: `Talk to ${actor.name}`, act: () => { this.send({ t: 'talk', id: actor.id }); mark(actor.pos.x, actor.pos.y); } });
        opts.push({ label: `Examine ${actor.name}`, tone: 'muted', act: () => exam(examineNpc(actor.npc!, actor.name)) });
      } else {
        const friend = this.self.friends.some(f => f.name.toLowerCase() === actor.name.toLowerCase());
        opts.push({ label: `Trade with ${actor.name}`, act: () => { this.send({ t: 'tradeAsk', id: actor.id }); mark(actor.pos.x, actor.pos.y); } });
        if (friend) opts.push({ label: `Message ${actor.name}`, act: () => this.startPm(actor.name) });
        else opts.push({ label: `Add ${actor.name} as a friend`, act: () => this.send({ t: 'friend', op: 'add', name: actor.name }) });
        opts.push({ label: 'Walk here', act: () => { this.send({ t: 'walk', x: actor.tileX, z: actor.tileZ }); mark(actor.pos.x, actor.pos.y); } });
        opts.push({ label: `Examine ${actor.name}`, tone: 'muted', act: () => exam(`${actor.name}, a fellow survivor. Combat level ${actor.combatLvl || '?'}.`) });
      }
    }
    if (!opts.length && hit.obj?.type === 'patch') {
      const o = hit.obj;
      title = PATCHES[o.kind as keyof typeof PATCHES]?.name ?? 'Patch';
      for (const po of patchOptions(this.char, o, this.map.regionAt(o.x, o.z)?.band)) opts.push({ label: po.label, sub: po.sub, act: () => { this.send({ t: 'farm', id: o.id, op: po.op, ...(po.crop ? { crop: po.crop } : {}) }); this.view.markObject(o); } });
      if (hit.tile) { const t = hit.tile; opts.push({ label: 'Walk here', act: () => { this.send({ t: 'walk', x: t[0], z: t[1] }); mark(t[0] + 0.5, t[1] + 0.5); } }); }
      opts.push({ label: `Examine ${title.toLowerCase()}`, tone: 'muted', act: () => exam(examinePatch(this.char, o)) });
    }
    if (!opts.length && hit.obj) {
      const o = hit.obj, [label] = this.hud.objLabel(o), cx = o.x + (o.w ?? 1) / 2, cz = o.z + (o.h ?? 1) / 2;
      title = o.props?.name ? String(o.props.name) : (RESOURCES as Record<string, { noun: string } | undefined>)[o.kind]?.noun ? (RESOURCES as Record<string, { noun: string }>)[o.kind].noun.replace(/^./, (c: string) => c.toUpperCase()) : o.kind.replace(/_/g, ' ').replace(/^./, (c: string) => c.toUpperCase());
      const clutter = (o.type === 'prop' && o.kind !== 'stall' && o.kind !== 'signpost') || (o.type === 'house' && !isNight(this.dayT));
      if (label && !clutter) opts.push({ label, act: () => { this.send({ t: 'interact', id: o.id }); if (o.type === 'resource' || o.type === 'station' || o.type === 'obstacle') this.view.markObject(o); else mark(cx, cz); } });
      if (hit.tile) { const t = hit.tile; opts.push({ label: 'Walk here', act: () => { this.send({ t: 'walk', x: t[0], z: t[1] }); mark(t[0] + 0.5, t[1] + 0.5); } }); }
      opts.push({ label: `Examine ${title.toLowerCase()}`, tone: 'muted', act: () => exam(examineObject(o)) });
    }
    if (!opts.length && hit.tile) { const t = hit.tile; title = this.map.regionAt(t[0], t[1])?.name ?? 'Here'; opts.push({ label: 'Walk here', act: () => { this.send({ t: 'walk', x: t[0], z: t[1] }); mark(t[0] + 0.5, t[1] + 0.5); } }); }
    if (opts.length) showMenu(x, y, title, opts);
  }

  private hover(dt: number) {
    if (this.build) return;
    this.hoverClock -= dt;
    if (this.hoverClock > 0 || !this.hoverAt) return;
    this.hoverClock = 0.09;
    const hit = this.view.pick(this.hoverAt.x, this.hoverAt.y);
    const app = $('#app').getBoundingClientRect();
    const actor = hit?.actor !== undefined ? this.actors.get(hit.actor) : undefined;
    if (actor?.mob && isCreatureKind(actor.mob)) {
      const d = creatureDef(actor.mob), pa = playerAttack(this.char);
      const chance = Math.round(hitChance(pa.acc, creatureDefence(d)) * 100);
      this.hud.tooltipText(`Attack ${d.name} · ${chance}% to hit, max ${pa.max}${d.aggressive ? ' · aggressive' : ''}`, this.hoverAt.x - app.left, this.hoverAt.y - app.top);
      return;
    }
    if (actor) { this.hud.tooltipText(actor.npc ? `Talk to ${actor.name}` : !actor.isSelf ? `Trade with ${actor.name}` : actor.name, this.hoverAt.x - app.left, this.hoverAt.y - app.top); return; }
    if (hit?.pile !== undefined) { const pi = this.view.combat.pileInfo(hit.pile); if (pi) this.hud.tooltipText(this.hud.pileLabel(pi), this.hoverAt.x - app.left, this.hoverAt.y - app.top); return; }
    if (hit?.trap !== undefined) { const t = this.self.traps[hit.trap]; if (t) { const mins = Math.floor((Date.now() - t.setAt) / 60000); this.hud.tooltipText(`Haul in your ${TOOLS[t.tool].name.toLowerCase()} · ${mins < 1 ? 'just set' : `set ${mins} min ago`}`, this.hoverAt.x - app.left, this.hoverAt.y - app.top); } return; }
    this.hud.tooltip(hit?.obj ?? null as MapObject | null, this.hoverAt.x - app.left, this.hoverAt.y - app.top);
  }

  private worldMap: WorldMap | null = null;
  private lastPmFrom: string | null = null;
  private stopTips: (() => void) | null = null;
  private lastPmTo: string | null = null;
  /** Open the chat line ready to message someone. */
  startPm(name: string) {
    const chat = $<HTMLInputElement>('#chat-input'); chat.value = `/w ${/\s/.test(name) ? `"${name}"` : name} `;
    chat.focus(); chat.setSelectionRange(chat.value.length, chat.value.length);
  }
  private mapClock = 0;
  private toggleMap(show?: boolean) {
    const mv = $('#mapview'), open = show ?? mv.hidden;
    mv.hidden = !open;
    if (!open || !this.maps || !this.worldMap) { this.worldMap?.hide(); return; }
    this.hud.closeStation();
    const s = this.mapState();
    this.worldMap.show(s);
    $('#map-stat').textContent = `${this.maps.chartedPercent()}% charted · ${this.self.landmarks.length} of ${this.map.objects.filter(o => o.type === 'landmark').length} landmarks`;
    $('#map-perk').textContent = s.surveyor ? 'Drag to move, scroll or pinch to zoom. Tap a place to see what it is; tap charted ground to walk there.' : 'Drag to move, scroll or pinch to zoom. Tap charted ground to walk there. Exploration 10 marks the resources you have seen.';
    this.renderMapLegend(s.surveyor);
  }
  /** The legend doubles as filters: tap a kind to hide or show it. */
  private renderMapLegend(surveyor = this.mapState().surveyor) {
    const box = $('#map-legend'), wm = this.worldMap; if (!wm) return;
    box.replaceChildren();
    for (const k of POI_KINDS) {
      if (k.kind === 'resource' && !surveyor) continue;
      const b = document.createElement('button'); b.type = 'button';
      const on = wm.filterState[k.kind];
      b.className = on ? '' : 'off'; b.setAttribute('aria-pressed', String(on));
      const i = document.createElement('i'); i.style.background = k.color; b.append(i, k.label);
      b.onclick = () => { wm.setFilter(k.kind, !wm.filterState[k.kind]); this.renderMapLegend(surveyor); };
      box.appendChild(b);
    }
  }

  private bindInput() {
    const cv = this.view.canvas;
    let down: { x: number; y: number; yaw: number; pitch: number; button: number; lx?: number; ly?: number } | null = null, dragging = false;
    // two fingers: pinch to zoom, twist to turn the camera, slide both up or down to tilt it
    let pinch: { d: number; dist: number; a: number; yaw: number; my: number; pitch: number } | null = null;
    const pointers = new Map<number, { x: number; y: number }>();
    // touch: press and hold to see what something is (the tooltip a mouse would show on hover)
    let press: number | undefined, longPressed = false, holdTip: number | undefined;
    const cancelPress = () => { if (press !== undefined) { clearTimeout(press); press = undefined; } };
    cv.addEventListener('pointerdown', e => {
      cancelPress(); longPressed = false;
      if (e.pointerType !== 'mouse' && pointers.size === 0) {
        const at = { x: e.clientX, y: e.clientY };
        press = window.setTimeout(() => {
          press = undefined; longPressed = true;
          // press and hold: the same menu a right-click gives
          if (!this.build) { this.openMenu(at.x, at.y); navigator.vibrate?.(12); return; }
          this.hoverAt = at; this.hoverClock = 0;
          clearTimeout(holdTip); holdTip = window.setTimeout(() => { this.hoverAt = null; this.hud.tooltip(null); }, 2200);
          navigator.vibrate?.(12);
        }, 450);
      }
    });
    cv.addEventListener('pointerdown', e => {
      audio.unlock();
      cv.focus({ preventScroll: true });
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      cv.setPointerCapture(e.pointerId);
      if (pointers.size === 2) { const [a, b] = [...pointers.values()]; pinch = { d: Math.hypot(a.x - b.x, a.y - b.y), dist: this.view.dist, a: Math.atan2(b.y - a.y, b.x - a.x), yaw: this.view.yaw, my: (a.y + b.y) / 2, pitch: this.view.pitch }; down = null; cancelPress(); return; }
      down = { x: e.clientX, y: e.clientY, yaw: this.view.yaw, pitch: this.view.pitch, button: e.button }; dragging = false;
    });
    cv.addEventListener('pointermove', e => {
      if (pointers.has(e.pointerId)) pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pinch && pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        this.view.dist = clamp((pinch.dist * pinch.d) / Math.max(20, Math.hypot(a.x - b.x, a.y - b.y)), ...this.view.camLimits.dist);
        let turn = Math.atan2(b.y - a.y, b.x - a.x) - pinch.a;
        if (turn > Math.PI) turn -= Math.PI * 2; if (turn < -Math.PI) turn += Math.PI * 2;
        this.view.yaw = pinch.yaw + turn; // the ground turns with your fingers
        this.view.pitch = clamp(pinch.pitch + (((a.y + b.y) / 2) - pinch.my) * 0.004, ...this.view.camLimits.pitch);
        this.hud.tooltip(null);
        return;
      }
      if (down) {
        const dx = e.clientX - down.x, dy = e.clientY - down.y;
        if (!dragging && Math.hypot(dx, dy) > 7) { dragging = true; cancelPress(); }
        // build mode: dragging slides the camera over the ground (turn it with the buttons or Q/E)
        if (dragging && this.build) { this.build.pan(e.clientX - (down.lx ?? down.x), e.clientY - (down.ly ?? down.y)); down.lx = e.clientX; down.ly = e.clientY; return; }
        if (dragging) { this.view.yaw = down.yaw - dx * 0.008; this.view.pitch = clamp(down.pitch + dy * 0.005, ...this.view.camLimits.pitch); this.hud.tooltip(null); return; }
      }
      if (e.pointerType === 'mouse') this.hoverAt = { x: e.clientX, y: e.clientY };
      if (this.build) this.build.hover(e.clientX, e.clientY);
    });
    const end = (e: PointerEvent) => {
      pointers.delete(e.pointerId);
      if (pinch) { if (pointers.size < 2) pinch = null; down = null; return; }
      cancelPress();
      if (down && !dragging && e.type === 'pointerup' && down.button !== 2 && !longPressed) this.click(e.clientX, e.clientY, e.shiftKey, e.altKey, e.pointerType !== 'mouse');
      // right-click (without dragging the camera round): the menu of everything you can do there
      if (down && !dragging && e.type === 'pointerup' && down.button === 2 && !this.build) this.openMenu(e.clientX, e.clientY);
      down = null; dragging = false;
    };
    cv.addEventListener('pointerup', end);
    cv.addEventListener('pointercancel', end);
    cv.addEventListener('pointerleave', () => { this.hoverAt = null; this.hud.tooltip(null); });
    cv.addEventListener('contextmenu', e => e.preventDefault());
    cv.addEventListener('wheel', e => { e.preventDefault(); this.view.dist = clamp(this.view.dist * (1 + Math.sign(e.deltaY) * 0.1), ...this.view.camLimits.dist); }, { passive: false });

    const chat = $<HTMLInputElement>('#chat-input');
    $('#chat-form').addEventListener('submit', e => {
      e.preventDefault();
      const text = chat.value.trim();
      // /w name message (or @name message) whispers a friend; /r message replies to the last whisper
      const w = text.match(/^(?:\/w(?:hisper)?|\/pm|\/m|@)\s*("([^"]+)"|\S+)\s+(.+)$/i), r = text.match(/^\/r(?:eply)?\s+(.+)$/i);
      if (r) { if (this.lastPmFrom) { this.send({ t: 'pm', to: this.lastPmFrom, text: r[1] }); this.lastPmTo = this.lastPmFrom; } else this.hud.log('Nobody has messaged you yet.', 'bad'); }
      else if (w) { const to = w[2] ?? w[1]; this.send({ t: 'pm', to, text: w[3] }); this.lastPmTo = to; }
      else if (text) this.send({ t: 'chat', text });
      chat.value = ''; chat.blur(); cv.focus({ preventScroll: true });
    });
    window.addEventListener('keydown', e => {
      if (document.activeElement === chat) { if (e.key === 'Escape') { chat.blur(); cv.focus(); } return; }
      if ((e.target as HTMLElement)?.tagName === 'INPUT') return;
      const k = e.key.toLowerCase();
      if (k === 'enter') { e.preventDefault(); chat.focus(); return; }
      if (k === 'q' || k === 'arrowleft') this.keys.l = true;
      if (k === 'e' || k === 'arrowright') this.keys.r = true;
      if (k === 'arrowup' || k === '=' || k === '+') this.view.dist = clamp(this.view.dist - 2, ...this.view.camLimits.dist);
      if (k === 'arrowdown' || k === '-') this.view.dist = clamp(this.view.dist + 2, ...this.view.camLimits.dist);
      if (k === 'm') this.toggleMap();
      if (k === 'r') this.send({ t: 'run', on: !this.self.run });
      if (k === 'c') this.send({ t: 'sneak', on: !this.self.sneak });
      if (k === 'escape') { if (this.trade.view) this.send({ t: 'tradeCancel' }); else if (!$('#mapview').hidden) this.toggleMap(false); else if (this.hud.dialogOpen) this.send({ t: 'choose', option: 'bye' }); else this.hud.closeStation(true); }
      if (/^[1-9]$/.test(k) && this.hud.chooseByNumber(Number(k))) return;
      if (k === ' ' && this.striking) { e.preventDefault(); this.strike(); return; }
      if (k === '1') this.hud.setTab('skills'); if (k === '2') this.hud.setTab('pack'); if (k === '3') this.hud.setTab('belt'); if (k === '4') this.hud.setTab('quests'); if (k === '5') this.hud.setTab('journal');
    });
    window.addEventListener('keyup', e => { const k = e.key.toLowerCase(); if (k === 'q' || k === 'arrowleft') this.keys.l = false; if (k === 'e' || k === 'arrowright') this.keys.r = false; });
    const hold = (sel: string, dir: number) => {
      const b = $(sel);
      b.addEventListener('pointerdown', e => { e.preventDefault(); this.view.yawVel = dir * 1.8; });
      for (const t of ['pointerup', 'pointerleave', 'pointercancel']) b.addEventListener(t, () => { this.view.yawVel = 0; });
      b.addEventListener('click', e => { if (e.detail === 0) this.view.yaw += dir * 0.4; });
    };
    hold('#btn-rotl', -1); hold('#btn-rotr', 1);
    $('#strike').addEventListener('pointerdown', e => { e.preventDefault(); e.stopPropagation(); this.strike(); });
    $('#btn-zin').addEventListener('click', () => { this.view.dist = clamp(this.view.dist - 3, ...this.view.camLimits.dist); });
    $('#btn-zout').addEventListener('click', () => { this.view.dist = clamp(this.view.dist + 3, ...this.view.camLimits.dist); });
    $('#minimap').addEventListener('click', () => this.toggleMap(true));
    $('#btn-map').addEventListener('click', () => this.toggleMap());
    $('#map-close').addEventListener('click', () => this.toggleMap(false));
    $('#mapview').addEventListener('click', e => { if (e.target === $('#mapview')) this.toggleMap(false); });
    $('#map-x').addEventListener('click', () => this.toggleMap(false));
    $('#map-zin').addEventListener('click', () => this.worldMap?.zoomBy(1.6));
    $('#map-zout').addEventListener('click', () => this.worldMap?.zoomBy(1 / 1.6));
    $('#map-me').addEventListener('click', () => this.worldMap?.centre());
    $('#map-fit').addEventListener('click', () => this.worldMap?.fit());
  }
}

/** Armour metal for a gear id (for drawing our own character before the server's look arrives). */
function creatureLook(id: string) { return (GEAR as Record<string, { metal: number }>)[id]?.metal ?? 0; }
